/**
 * LF360-1874 — what a counter task's two fields are, on the way to the server.
 *
 * The server is authoritative and refuses the same things
 * (`lib/utils/counterTask.js` there). This exists so the admin finds out while
 * they are typing rather than on save, and so the payload carries `null` where
 * the form carries an empty string — an empty Goal box means "no goal", and a
 * goal of 0 is not a thing.
 *
 * Deliberately NOT a second opinion. Every rule here is one the server also
 * applies; the two disagreeing would be worse than having no client check at
 * all, because the form would accept something the save then refuses with
 * nothing on screen to explain it.
 */

export const COUNTER = 'counter';

export const TASK_TYPE_LABELS = {
  regular: 'Regular task',
  survey: 'Survey task',
  [COUNTER]: 'Counter task',
};

export const TASK_TYPE_OPTIONS = Object.entries(TASK_TYPE_LABELS)
  .map(([value, label]) => ({ value, label }));

/**
 * The value the API is sent. Empty stays empty — `null`, not `""` — because the
 * column is nullable and `""` is not a unit.
 */
export const unitsToPayload = (units) => {
  const trimmed = typeof units === 'string' ? units.trim() : '';

  return trimmed === '' ? null : trimmed;
};

/**
 * The goal, as a number or nothing at all. An empty box is a counter without a
 * goal, which is a valid configuration and the reason this cannot simply be
 * `Number(value) || null`: that would also swallow a typo of `0`, which must be
 * refused rather than quietly turned into "no goal".
 */
export const goalToPayload = (goal) => {
  if (goal === null || goal === undefined) return null;

  const raw = String(goal).trim();

  if (raw === '') return null;

  const parsed = Number(raw);

  return Number.isFinite(parsed) ? parsed : raw;
};

/**
 * What is wrong with the two fields, or nothing.
 *
 * Returns the same codes the server uses, so a message shown here and an error
 * returned from a save cannot describe the same problem two different ways.
 */
export const counterConfigurationError = ({ taskType, counterUnits, counterGoal }) => {
  if (taskType !== COUNTER) return null;

  if (unitsToPayload(counterUnits) === null) return 'COUNTER_UNITS_REQUIRED';

  const goal = goalToPayload(counterGoal);

  if (goal === null) return null;

  return Number.isSafeInteger(goal) && goal > 0 ? null : 'COUNTER_GOAL_INVALID';
};

export const COUNTER_ERROR_MESSAGES = {
  COUNTER_UNITS_REQUIRED: 'Units are required for a counter task.',
  COUNTER_GOAL_INVALID: 'The goal must be a whole number above zero, or empty.',
};
